import React, { useState, useEffect } from 'react';
import { doc, getDoc, getDocs, collection, updateDoc, addDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import '../../styles/FactionRosterExperience.css';
import { buildFactionMemberFields, getFactionStoredWrestlers, getFactionWrestlerIds } from '../../utils/factionWrestlers';
import { buildFactionLineupUpdateAfterSwap, buildSwapReplacementMap } from '../../utils/factionLineup';
import { getFactionMemberSwapSummary } from '../../utils/factionScoring';

export default function SwapWrestler({ factionId, onSwapped }) {
  const [faction, setFaction] = useState(null);
  const [wrestlers, setWrestlers] = useState([]);
  const [availableWrestlers, setAvailableWrestlers] = useState([]);
  const [releaseId, setReleaseId] = useState('');
  const [assignId, setAssignId] = useState('');
  const [message, setMessage] = useState('');
  const [roster, setRoster] = useState(null);
  useEffect(() => {
    async function load() {
      const fSnap = await getDoc(doc(db, 'factions', factionId));
      if (!fSnap.exists()) return;
      const data = fSnap.data();
      setFaction({ id: fSnap.id, ...data });
      if (data.rosterId) {
        const rosterSnap = await getDoc(doc(db, 'rosters', data.rosterId));
        setRoster(rosterSnap.exists() ? { id: rosterSnap.id, ...rosterSnap.data() } : null);
      }

      const wSnap = await getDocs(collection(db, 'wrestlers'));
      const allWrestlers = wSnap.docs
        .map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        .sort((a, b) => a.name.localeCompare(b.name));

      const factionsQuery = data.rosterId
        ? query(collection(db, 'factions'), where('rosterId', '==', data.rosterId))
        : query(collection(db, 'factions'), where('roster', '==', data.roster));
      const factionsSnap = await getDocs(factionsQuery);
      const factionsData = factionsSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }));

      const assignedWrestlerIds = new Set();
      factionsData.forEach((currentFaction) => {
        getFactionWrestlerIds(currentFaction).forEach((id) => assignedWrestlerIds.add(id));
      });

      const currentFactionWrestlerIds = new Set(getFactionWrestlerIds({ id: fSnap.id, ...data }));
      setWrestlers(allWrestlers.filter((wrestler) => currentFactionWrestlerIds.has(wrestler.id)));
      setAvailableWrestlers(
        allWrestlers.filter(
          (wrestler) => !assignedWrestlerIds.has(wrestler.id) && wrestler.active !== false && !wrestler.isGuest
        )
      );
    }

    load();
  }, [factionId]);

  const handleSwap = async () => {
    if (!releaseId || !assignId) {
      setMessage('Seleziona entrambi i wrestler');
      return;
    }

    let isWithinFirstDay = false;
    if (faction && faction.createdAt) {
      let creationDate;
      if (faction.createdAt instanceof Date) {
        creationDate = faction.createdAt;
      } else if (faction.createdAt.seconds) {
        creationDate = new Date(faction.createdAt.seconds * 1000);
      } else if (typeof faction.createdAt === 'string' || typeof faction.createdAt === 'number') {
        creationDate = new Date(faction.createdAt);
      }
      if (creationDate) {
        const now = new Date();
        const diffMs = now - creationDate;
        const diffHours = diffMs / (1000 * 60 * 60);
        isWithinFirstDay = diffHours < 24;
      }
    }

    const old = getFactionStoredWrestlers(faction).find((member) => member.id === releaseId);
    if (!old) {
      setMessage('Wrestler da svincolare non trovato');
      return;
    }

    const oldWrestlerSnap = await getDoc(doc(db, 'wrestlers', releaseId));
    if (!oldWrestlerSnap.exists()) {
      setMessage('Dati del wrestler da svincolare non disponibili');
      return;
    }

    const currentScore = oldWrestlerSnap.data().score || 0;
    const swapSummary = getFactionMemberSwapSummary({
      faction,
      roster,
      wrestlerId: releaseId,
      currentScore,
      startScore: old.startScore || 0,
      accumulatedPoints: old.accumulatedPoints || 0,
      status: old.status || null,
    });
    const storedAccumulatedPoints = old.accumulatedPoints || 0;
    const newAccum = (faction.accumulatedPoints || 0) + swapSummary.currentPoints;
    const newSumPoints = (faction.sumPoints || 0) + swapSummary.savedPoints;

    const newSnap = await getDoc(doc(db, 'wrestlers', assignId));
    if (!newSnap.exists()) {
      setMessage('Dati del nuovo wrestler non disponibili');
      return;
    }

    const newData = newSnap.data();
    const startNew = newData.score || 0;

    const priceOut = old.cost;
    const priceIn = priceOut;
    const swapReplacements = buildSwapReplacementMap([releaseId], [assignId]);

    const updatedWrestlers = getFactionStoredWrestlers(faction).map((member) =>
      member.id === releaseId
        ? {
            ...member,
            id: assignId,
            wrestlerId: assignId,
            cost: priceIn,
            startScore: startNew,
            currentScore: startNew,
            accumulatedPoints: 0,
            status: member.status || null,
          }
        : member
    );
    const lineupUpdate = buildFactionLineupUpdateAfterSwap({
      faction,
      roster,
      updatedWrestlers,
      replacements: swapReplacements,
      now: new Date(),
    });

    const newCredits = faction.creditsLeft;

    const updatePayload = {
      ...buildFactionMemberFields(updatedWrestlers),
      ...(lineupUpdate || {}),
      accumulatedPoints: newAccum,
      sumPoints: newSumPoints,
      creditsLeft: newCredits,
      updatedAt: serverTimestamp(),
    };

    if (!isWithinFirstDay) {
      updatePayload.swapsUsed = (faction.swapsUsed || 0) + 1;
    }

    await updateDoc(doc(db, 'factions', factionId), updatePayload);

    await addDoc(collection(db, 'factions', factionId, 'swaps'), {
      wrestlerOut: doc(db, 'wrestlers', releaseId),
      wrestlerIn: doc(db, 'wrestlers', assignId),
      scoreOut: swapSummary.savedPoints,
      currentPointsOut: swapSummary.currentPoints,
      accumulatedPointsOut: storedAccumulatedPoints,
      totalPointsOut: swapSummary.totalPoints,
      sumPointsOut: swapSummary.savedPoints,
      scoreIn: startNew,
      priceOut,
      priceIn,
      date: serverTimestamp(),
      freeSwap: isWithinFirstDay,
      operationType: 'swap'
    });
    const nextFactionState = {
      ...faction,
      ...buildFactionMemberFields(updatedWrestlers),
      ...(lineupUpdate || {}),
      accumulatedPoints: newAccum,
      sumPoints: newSumPoints,
      creditsLeft: newCredits,
      swapsUsed: updatePayload.swapsUsed ?? faction.swapsUsed ?? 0,
    };
    const releasedWrestler = { id: releaseId, ...oldWrestlerSnap.data() };
    const assignedWrestler = { id: assignId, ...newData };

    setFaction(nextFactionState);
    setWrestlers((prev) =>
      [...prev.filter((wrestler) => wrestler.id !== releaseId), assignedWrestler]
        .sort((a, b) => a.name.localeCompare(b.name))
    );
    setAvailableWrestlers((prev) =>
      [...prev.filter((wrestler) => wrestler.id !== assignId), releasedWrestler]
        .sort((a, b) => a.name.localeCompare(b.name))
    );

    setMessage(
      isWithinFirstDay
        ? `Scambio gratuito! Conservati ${swapSummary.savedPoints} punti. Totale sumPoints: ${newSumPoints}`
        : `Scambiato! Conservati ${swapSummary.savedPoints} punti. Totale sumPoints: ${newSumPoints}`
    );

    onSwapped?.(nextFactionState);
    setReleaseId('');
    setAssignId('');
  };

  if (!faction) {
    return (
      <div className="frx-empty">
        <div className="spinner-border text-brand-secondary" role="status"></div>
        <p className="mb-0">Caricamento scambio wrestler...</p>
      </div>
    );
  }

  return (
    <div className="frx-panel-stack swap-wrestler">

      <div>

            <p className="frx-helper mb-1">Effettua una sostituzione diretta mantenendo lo stesso costo sul nuovo wrestler.</p>

        <div className="row g-3">
          <div className="col-md-6">
            <div className="h-100">
              <label className="form-label text-white fw-semibold">Wrestler da svincolare</label>
              <select
                className="form-select"
                value={releaseId}
                onChange={(event) => setReleaseId(event.target.value)}
              >
                <option value="">Seleziona wrestler...</option>
                {getFactionStoredWrestlers(faction)?.map((member) => (
                  <option key={member.id} value={member.id}>
                    {wrestlers.find((wrestler) => wrestler.id === member.id)?.name || member.name || 'Sconosciuto'} ({member.cost}$)
                  </option>
                ))}
              </select>
              <div className="frx-helper mt-2">Seleziona il wrestler della tua fazione che vuoi sostituire.</div>
            </div>
          </div>

          <div className="col-md-6">
            <div className=" h-100">
              <label className="form-label text-white fw-semibold">Nuovo wrestler</label>
              <select
                className="form-select"
                value={assignId}
                onChange={(event) => setAssignId(event.target.value)}
              >
                <option value="">Seleziona sostituto...</option>
                {availableWrestlers.length > 0 ? (
                  availableWrestlers.map((wrestler) => (
                    <option key={wrestler.id} value={wrestler.id}>
                      {wrestler.name}
                    </option>
                  ))
                ) : (
                  <option disabled>Nessun wrestler disponibile</option>
                )}
              </select>
              <small className="frx-helper d-block mt-2">
                Mostrati solo wrestler non assegnati ad altre fazioni del roster
              </small>

              {assignId && (
                <div className="mt-3 alert alert-warning frx-alert bg-warning bg-opacity-10 text-light border-0 mb-0">
                  Verra applicato lo stesso prezzo del wrestler da svincolare
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="d-grid mt-3">
          <button
            className="btn btn-outline-primary w-100"
            onClick={handleSwap}
            disabled={!releaseId || !assignId}
            type="button"
          >
            <i className="bi bi-arrow-left-right me-2"></i>
            Conferma Scambio
          </button>
        </div>

        {message && (
          <div className={`alert ${message.includes('Seleziona') || message.includes('Errore') ? 'alert-danger' : 'alert-info'} frx-alert mt-3 mb-0 border-0`}>
            <i className={`bi ${message.includes('Seleziona') || message.includes('Errore') ? 'bi-exclamation-circle' : 'bi-info-circle'} me-2`}></i>
            {message}
          </div>
        )}
      </div>
    </div>
  );
}
